import QRCode from 'qrcode';
import { useEffect, useState, type CSSProperties } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { errorMessage } from '@/lib/format';
import { QrActions } from './QrActions';

import {
  downloadPledge,
  signUrl,
  useSignLink,
  useSignLinkWrite,
  usePledgeDelete,
  usePledges,
  type BoardRow,
  type PledgeRow,
} from './safeSignApi';

/**
 * 안전교육일지의 이수확인서 · 이행각서 QR 창(DOC-129 §5) — 목록의 QR 칸에서 띄운다. 아래에 낸 사람 명단.
 * 열면 링크를 연다(없으면 만들고, 있으면 지금부터 24시간 다시).
 * 교육장 화면에 띄우거나 그림으로 내려받아 붙인다. 버튼은 제목 줄 — [QR 닫기] · [QR 그림 저장].
 */
export function SignQrModal({ journalId, title, onClose }: { journalId: string; title: string; onClose: () => void }) {
  const link = useSignLink(journalId);
  const write = useSignLinkWrite(journalId);
  const pledges = usePledges(journalId, true);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 창을 열면 링크를 연다 — [QR] 을 누른 것이 곧 「지금 받겠다」 이다.
  useEffect(() => {
    write.mutate('open', { onError: (cause) => setError(errorMessage(cause)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journalId]);

  const data = write.data ?? link.data ?? null;
  const url = data ? signUrl(data.token) : null;
  useEffect(() => {
    if (!url) return;
    let alive = true;
    void QRCode.toDataURL(url, { width: 720, margin: 1, errorCorrectionLevel: 'M' }).then((value) => {
      if (alive) setQr(value);
    });
    return () => {
      alive = false;
    };
  }, [url]);

  const until = data ? new Date(data.expiresAt).toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <Modal
      title="이수확인서 · 이행각서 QR"
      onClose={onClose}
      maxWidth="760px"
      actions={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <QrActions
            url={url}
            qr={qr}
            name={`안전교육_${title}`}
            poster={{ title: '안전교육 이수확인서', siteName: title, lines: ['휴대폰 카메라로 QR 을 비춥니다', '이수확인서 · 이행각서를 읽고 적습니다', '서명하고 제출합니다'] }}
          />
          {data?.open ? (
            <button className="btn-secondary" disabled={write.isPending} onClick={() => write.mutate('close', { onError: (cause) => setError(errorMessage(cause)) })}>
              QR 닫기
            </button>
          ) : (
            <button className="btn-primary" disabled={write.isPending} onClick={() => write.mutate('open', { onError: (cause) => setError(errorMessage(cause)) })}>
              다시 열기
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-3" style={{ textAlign: 'center' }}>
        {error && <Notice tone="warning">{error}</Notice>}
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div style={{ position: 'relative', width: '320px', height: '320px', margin: '0 auto' }}>
          {qr && <img src={qr} alt="QR" style={{ width: '320px', height: '320px', imageRendering: 'pixelated', opacity: data?.open ? 1 : 0.25 }} />}
          {data && !data.open && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '18px' }}>
              닫힘
            </div>
          )}
        </div>
        {url && (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', wordBreak: 'break-all' }}>
            {url}
          </div>
        )}
        <div style={{ fontSize: '13px' }}>
          {data?.open ? `${until} 까지 · ` : ''}낸 사람 <b>{pledges.data?.length ?? data?.submitted ?? 0}</b>명
        </div>
        {(pledges.data?.length ?? 0) > 0 && (
          <div style={{ textAlign: 'left' }}>
            <PledgeTable journalId={journalId} rows={pledges.data ?? []} onError={setError} />
          </div>
        )}
      </div>
    </Modal>
  );
}

/** 목록 줄의 QR 칸 — 상태 · 낸 사람 수. 누르면 그 일지의 QR 창(줄 클릭의 편집 창과 갈린다). */
export function SignCell({ row, onOpen }: { row: BoardRow | undefined; onOpen: () => void }) {
  const link = row?.link ?? null;
  const count = row?.pledges.length ?? 0;
  return (
    <button
      type="button"
      className="btn-secondary"
      style={{ fontSize: '12px', padding: '2px 10px', whiteSpace: 'nowrap' }}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      {/* 단추 이름은 늘 「QR」(사용자 지시 2026-10-23). 받는 중이면 초록 점 하나. */}
      {link?.open && (
        <span
          aria-label="받는 중"
          style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', marginRight: '5px', verticalAlign: 'middle' }}
        />
      )}
      QR
      {count > 0 && ` · ${count}명`}
    </button>
  );
}

/** QR 창 아래의 낸 사람 명단 — 줄마다 [PDF](확인서 + 각서 두 쪽) · [✕]. 건강 정보는 여기 싣지 않는다(PDF 에만). */
function PledgeTable({ journalId, rows, onError }: { journalId: string; rows: PledgeRow[]; onError: (message: string | null) => void }) {
  const remove = usePledgeDelete(journalId);
  const [busy, setBusy] = useState<string | null>(null);
  const cell: CSSProperties = { padding: '4px 6px', borderTop: '1px solid var(--border)', fontSize: '12px' };

  const pdf = async (row: PledgeRow) => {
    onError(null);
    setBusy(row.id);
    try {
      await downloadPledge(journalId, row.id, `안전교육이수확인서_${row.name}`);
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={{ ...cell, fontWeight: 600 }}>{row.name}</td>
                <td style={{ ...cell, color: 'var(--text-secondary)' }}>{[row.affiliation, row.trade].filter(Boolean).join(' · ')}</td>
                <td style={{ ...cell, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {new Date(row.submittedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                </td>
                <td style={{ ...cell, whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button type="button" className="btn-secondary" style={{ fontSize: '11px', padding: '2px 8px' }} disabled={busy === row.id} onClick={() => void pdf(row)}>
                    {busy === row.id ? '…' : 'PDF'}
                  </button>
                  <button
                    type="button"
                    aria-label="확인서 삭제"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 6px' }}
                    onClick={() => {
                      if (!window.confirm(`${row.name} 의 확인서를 지울까요?`)) return;
                      remove.mutate(row.id, { onError: (cause) => onError(errorMessage(cause)) });
                    }}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
  );
}
